import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import Link from 'flarum/common/components/Link';
import Button from 'flarum/common/components/Button';
import LogInModal from 'flarum/forum/components/LogInModal';
import extractText from 'flarum/common/utils/extractText';

import { N8N_TAG_SLUG, emailConfirmed, seesN8nTag } from '../utils/routeState';
import { legalLinkParams } from '../utils/legalLinks';
import {
  keepSelectionOnCheckLabel,
  rememberCheckLabelPress,
  undoCheckLabelDoubleClick,
} from '../utils/selectableCheckLabel';
import ConfirmEmailNotice from './ConfirmEmailNotice';
import {
  conflictGate,
  conflictMessage,
  contextMax,
  emptyValues,
  gate,
  hasN8nAccess,
  isEmailUnconfirmedConflict,
  mapServerErrors,
  requestOptions,
  setGate,
  toggleMulti,
  validate,
  FIELD_ORDER,
  MULTI,
  type Errors,
  type FieldName,
  type FormValues,
  type RequestOptions,
} from '../utils/requestForm';

/**
 * M3 · Form B — solicitud de acceso a n8n Chile, y sus estados M4 / M4c.
 *
 * SOLO CON SESION, y eso es la regla de producto, no una comodidad tecnica: la Spec §10 decide
 * que «nadie se registra directo al grupo cerrado» y el checklist 19 lo exige. Al invitado no
 * se le pinta el formulario ni un formulario recortado: se le manda a la puerta A.
 *
 * UNA SOLA PANTALLA, SEIS ESTADOS. Todos viven aqui porque todos responden a la misma pregunta
 * («¿en que va mi acceso al grupo?») y el estado que manda llega en el mismo sitio para los
 * seis (`umbralComunidadGate` del payload del foro). Repartirlos entre rutas obligaria a
 * decidir la ruta ANTES de saber el estado.
 *
 * Estructura del formulario, literal del mockup M3: origen · Herramientas (low-code +
 * automatizacion) · Niveles (Excel + n8n) · Motivo (que buscas + contexto + normas).
 */
export default class SolicitudPage extends Page {
  values!: FormValues;
  errors: Errors = {};
  unmapped: string[] = [];
  loading = false;
  options!: RequestOptions;
  max!: number;

  /** Acaba de enviarse en ESTA visita: cambia el encabezado de M4 a «Solicitud recibida». */
  justSent = false;

  /** Tras un rechazo, la persona pidio volver a intentarlo: se muestra el formulario. */
  retrying = false;

  /**
   * El servidor acaba de contestar 409 `umbral_email_unconfirmed` (PKG-FORO-PRODUCT-GATES · D).
   *
   * Es la RED del fail-open de `emailConfirmed()`: si `isEmailConfirmed` no viajaba en el
   * payload, la pantalla dejo enviar y el veto llego del servidor. Se anota aqui para que
   * `body()` pase al mismo estado que habria pintado de saberlo antes, en vez de devolver a la
   * persona a un formulario que ya sabemos que va a rebotar.
   */
  rejectedAsUnconfirmed = false;

  oninit(vnode: any) {
    super.oninit(vnode);

    app.setTitle(extractText(app.translator.trans('umbral-comunidad.forum.solicitud.meta_title')));
    app.setTitleCount(0);

    this.options = requestOptions();
    this.max = contextMax();
    this.values = emptyValues();
  }

  view() {
    return (
      <div className="CmRequest-container container">
        <div className="CmRequest">{this.body()}</div>
      </div>
    );
  }

  body() {
    const state = gate();

    // Sin sesion: puerta A primero (checklist 19). Antes que nada, porque sin sesion el resto
    // del estado que llega es el del invitado y no dice nada de esta persona.
    if (!app.session.user) return this.guest();

    /*
      CUENTA SIN CONFIRMAR — antes que el formulario (PKG-FORO-PRODUCT-GATES · decisiones D+E).

      El servidor ya cortaba aqui: `RequestGroupAccessHandler` lanza 409
      `umbral_email_unconfirmed` como PRIMERA puerta tras `assertRegistered()`, o sea antes de
      resolver el grupo y antes de validar. Lo que faltaba era decirlo en pantalla: quien se
      acababa de dar de alta rellenaba ocho campos y recibia «¡Caramba! Algo salio mal».

      Va DESPUES de `guest()` (sin sesion manda su propio estado) y ANTES de `unavailable()`:
      a quien no ha confirmado le da igual si el grupo esta configurado — no puede solicitar de
      ninguna forma, y «confirma tu correo» es la unica accion que le desbloquea.

      Se mantiene DESPUES de los estados de exito? No: `justSent`/`approved`/`pending` viven mas
      abajo justo porque una cuenta sin confirmar no puede haber llegado a ninguno de ellos por
      esta pantalla; y si un admin la metio al grupo a mano, `hasN8nAccess()` seguiria siendo
      cierto pero el resto de puertas del servidor le seguirian diciendo que no. Primero
      confirmar; lo demas se cuenta despues, con el estado ya limpio.

      Gemelo exacto de `ProponerPage.body()`, con el mismo aviso reutilizable y su reenvio.
    */
    if (!emailConfirmed() || this.rejectedAsUnconfirmed) return this.unconfirmed();

    // Sin grupo configurado no hay nada que solicitar. Se dice; no se esconde el boton y ya.
    if (!state.configured) return this.unavailable();

    if (this.justSent) return this.pending(true);

    // TENER EL ACCESO gana sobre cualquier solicitud: es el hecho, lo demas es su historia.
    //
    // `hasN8nAccess()` y no `state.isMember` (PKG-FORO-UX-BUNDLE · frente D): al admin, que ve
    // la categoria sin pertenecer al grupo, esta pantalla le respondia «tu solicitud esta en
    // revision» sobre un acceso que ya tiene. El copy que sale ahora es `member_*` («tu cuenta
    // ya tiene acceso al grupo»), que es cierto para las dos formas de tenerlo.
    if (hasN8nAccess()) return this.approved(state.request?.status === 'approved');

    if (state.request?.status === 'pending') return this.pending(false);

    if (state.request?.status === 'rejected' && !this.retrying) return this.rejected();

    return this.form();
  }

  // --- estados ------------------------------------------------------------------------

  /** Puerta B publica: NO registra, lleva al alta Umbral (Spec §10, checklist 19). */
  guest() {
    const t = (key: string) => this.t(key);

    return (
      <div className="CmRequest-state" data-cm-state="guest">
        <span className="CmRequest-ic CmRequest-ic--n8n">🔒</span>
        <h1 className="CmRequest-title">{t('guest_title')}</h1>
        <p className="CmRequest-sub">{t('guest_body')}</p>

        <Link className="Button Button--primary CmRequest-submit" href={app.route('umbral-comunidad.registro')}>
          {t('guest_cta')}
        </Link>
        <Button
          className="Button Button--link CmRequest-secondary"
          onclick={() => app.modal.show(LogInModal)}
        >
          {t('guest_login')}
        </Button>
      </div>
    );
  }

  /**
   * Cuenta creada pero sin confirmar: el foro la trata como invitada (ver `body()`).
   *
   * Se reutiliza `ConfirmEmailNotice` —el mismo aviso del exito del alta y de
   * `/proponer-encuentro`— porque trae la UNICA accion que desbloquea de verdad: el reenvio,
   * con su 429 tratado como estado esperado y no como fallo. Un texto sin salida en este punto
   * manda a la persona a una bandeja de entrada donde a lo mejor no hay nada.
   *
   * El titulo es el especifico de esta pantalla («…para pedir acceso»), que dice mejor QUE es
   * lo que esta bloqueado que el generico del componente.
   */
  unconfirmed() {
    return (
      <div className="CmRequest-state" data-cm-state="unconfirmed">
        <ConfirmEmailNotice heading="h1" title={this.t('unconfirmed_title')} body={this.t('unconfirmed_body')} />
        {this.backToGroupPage()}
      </div>
    );
  }

  unavailable() {
    return (
      <div className="CmRequest-state" data-cm-state="unavailable">
        <span className="CmRequest-ic CmRequest-ic--warn">!</span>
        <h1 className="CmRequest-title">{this.t('unavailable_title')}</h1>
        <p className="CmRequest-sub">{this.t('unavailable_body')}</p>
        {this.backToGroupPage()}
      </div>
    );
  }

  /** M4 — solicitud recibida / en revision. */
  pending(fresh: boolean) {
    const t = (key: string) => this.t(key);

    return (
      <div className="CmRequest-state" data-cm-state="pending">
        <span className="CmRequest-ic">✓</span>
        <h1 className="CmRequest-title">{fresh ? t('pending_title_fresh') : t('pending_title')}</h1>
        <p className="CmRequest-sub">{t('pending_body')}</p>

        <Link className="Button Button--primary CmRequest-submit" href={app.route('umbral-comunidad.discussions')}>
          {t('pending_cta')}
        </Link>
      </div>
    );
  }

  /**
   * M4c — aprobada.
   *
   * `viaRequest` distingue «tu solicitud fue aprobada» de «ya eres parte»: a quien un admin
   * metio a mano en el grupo no se le puede hablar de una solicitud que nunca envio.
   *
   * El CTA apunta a la categoria SOLO si la sesion la ve de verdad (`seesN8nTag`). La
   * pertenencia al grupo y el permiso `tagN.viewForum` son cosas distintas — es exactamente el
   * agujero que T06-OPS encontro en produccion —, asi que si el permiso no esta puesto se
   * ofrece la pagina del grupo en vez de un enlace a un 404.
   */
  approved(viaRequest: boolean) {
    const t = (key: string) => this.t(key);

    return (
      <div className="CmRequest-state" data-cm-state="approved">
        <span className="CmRequest-ic CmRequest-ic--n8n">✓</span>
        <h1 className="CmRequest-title">{viaRequest ? t('approved_title') : t('member_title')}</h1>
        <p className="CmRequest-sub">{viaRequest ? t('approved_body') : t('member_body')}</p>

        {seesN8nTag() ? (
          <Link className="cm-btn-n8n CmRequest-submit" href={app.route('tag', { tags: N8N_TAG_SLUG })}>
            {t('approved_cta')}
          </Link>
        ) : (
          this.backToGroupPage()
        )}
      </div>
    );
  }

  rejected() {
    const t = (key: string) => this.t(key);

    return (
      <div className="CmRequest-state" data-cm-state="rejected">
        <span className="CmRequest-ic CmRequest-ic--warn">!</span>
        <h1 className="CmRequest-title">{t('rejected_title')}</h1>
        <p className="CmRequest-sub">{t('rejected_body')}</p>

        {/* Se puede volver a solicitar: la fila decidida libera el hueco unico de
            `pending_key` (ver la migracion), asi que el servidor lo permite de verdad. */}
        <Button
          className="Button Button--primary CmRequest-submit"
          data-cm-retry="1"
          onclick={() => {
            this.retrying = true;
            this.values = emptyValues();
            this.errors = {};
          }}
        >
          {t('rejected_cta')}
        </Button>
        {this.backToGroupPage()}
      </div>
    );
  }

  backToGroupPage() {
    return (
      <Link className="Button Button--link CmRequest-secondary" href={app.route('umbral-comunidad.n8nChile')}>
        {this.t('back_to_group')}
      </Link>
    );
  }

  // --- formulario ---------------------------------------------------------------------

  form() {
    const t = (key: string) => this.t(key);
    const options = this.options;
    const errorCount = Object.keys(this.errors).length;

    return (
      // `novalidate` por la misma razon que en el Form A: con la validacion nativa activa el
      // navegador se queda el `submit`, y entonces no hay barra de errores ni foco al primero.
      <form className="CmRequest-form" novalidate={true} onsubmit={this.onsubmit.bind(this)}>
        <span className="cm-chip cm-closed CmRequest-chip">{t('chip')}</span>
        <h1 className="CmRequest-title">{t('title')}</h1>

        {/* Checklist 18: con QUE cuenta se solicita, a la vista y antes de nada. */}
        {this.identity()}

        {errorCount > 0 && (
          <div className="CmRequest-errorbar" role="alert" data-cm-errorbar={String(errorCount)}>
            {t2('error_bar', { count: errorCount })}
          </div>
        )}
        {this.unmapped.map((message) => (
          <div className="CmRequest-errorbar" role="alert">
            {message}
          </div>
        ))}

        {this.select('origin', t('label_origin'), options.origin)}

        {this.section('tools', t('section_tools'))}
        {this.chips('lowcode', t('label_lowcode'), options.lowcode)}
        {this.chips('automation', t('label_automation'), options.automation)}

        {this.section('levels', t('section_levels'))}
        {this.select('excel', t('label_excel'), options.excel)}
        {this.select('n8n', t('label_n8n'), options.n8n)}

        {this.section('reason', t('section_reason'))}
        {this.chips('goals', t('label_goals'), options.goals)}
        {this.context()}
        {this.checkbox()}

        {/* Nunca deshabilitado (misma nota del mockup M2b): siempre explica que falta. */}
        <Button className="Button CmRequest-submit cm-btn-n8n-filled" type="submit" loading={this.loading}>
          {t('submit')}
        </Button>

        <p className="CmRequest-legal">{t('legal')}</p>
      </form>
    );
  }

  /**
   * «Solicitas con tu cuenta: Nombre · correo» (checklist 18).
   *
   * El correo importa tanto como el nombre: el mockup lo dice explicitamente («Si vienes de un
   * evento, es el mismo correo del registro»), porque es la unica forma de que quien se
   * inscribio a un encuentro con otra direccion se de cuenta ANTES de enviar.
   */
  identity() {
    const user = app.session.user!;
    const email = typeof (user as any).email === 'function' ? (user as any).email() : null;

    return (
      <p className="CmRequest-sub CmRequest-identity" data-cm-identity="1">
        {app.translator.trans('umbral-comunidad.forum.solicitud.identity', {
          name: <b>{user.displayName()}</b>,
          email: <b>{email || user.username()}</b>,
        })}
      </p>
    );
  }

  section(key: string, label: any) {
    return (
      <div className="CmRegister-section" data-cm-section={key}>
        {label}
      </div>
    );
  }

  select(name: FieldName, label: any, values: string[]) {
    const error = this.errors[name];

    return (
      <div className={'CmRegister-field' + (error ? ' CmRegister-field--error' : '')} data-cm-field={name}>
        <label htmlFor={'cm-req-' + name}>
          {label}
          <span className="CmRegister-req"> *</span>
        </label>
        <select
          id={'cm-req-' + name}
          className="FormControl CmRegister-select"
          name={name}
          disabled={this.loading}
          aria-invalid={error ? 'true' : undefined}
          value={this.values[name] as string}
          onchange={(e: any) => this.set(name, e.target.value)}
        >
          <option value="">{this.t('select_empty')}</option>
          {values.map((value) => (
            <option value={value}>{this.optionLabel(name, value)}</option>
          ))}
        </select>
        {this.message(name)}
      </div>
    );
  }

  /**
   * Chips multi-seleccion. Todas las del Form B son multi (las de valor unico del mockup son
   * `<select>`), y la exclusividad de «Ninguna aun» la resuelve `toggleMulti`.
   */
  chips(name: FieldName, label: any, values: string[]) {
    const error = this.errors[name];
    const selected = this.values[name] as string[];

    return (
      <div className={'CmRegister-field' + (error ? ' CmRegister-field--error' : '')} data-cm-field={name}>
        <span className="CmRegister-label">
          {label}
          <span className="CmRegister-req"> *</span>
        </span>
        <div className="CmRegister-opts CmRequest-opts" role="group">
          {values.map((value) => {
            const on = selected.indexOf(value) >= 0;

            return (
              <button
                type="button"
                className={'CmRegister-opt CmRequest-opt' + (on ? ' is-on' : '')}
                data-cm-opt={value}
                aria-pressed={on ? 'true' : 'false'}
                disabled={this.loading}
                onclick={() => {
                  this.values[name] = toggleMulti(selected, value) as any;
                  this.clear(name);
                }}
              >
                {this.optionLabel(name, value)}
              </button>
            );
          })}
        </div>
        {this.message(name)}
      </div>
    );
  }

  context() {
    const error = this.errors.context;
    const left = this.max - this.values.context.length;

    return (
      <div className={'CmRegister-field' + (error ? ' CmRegister-field--error' : '')} data-cm-field="context">
        <label htmlFor="cm-req-context">{this.t('label_context')}</label>
        <textarea
          id="cm-req-context"
          className="FormControl CmRequest-textarea"
          name="context"
          rows={3}
          /* `maxlength` frena al escribir; la validacion sigue existiendo porque un cliente
             que no sea este navegador puede mandar lo que quiera. */
          maxlength={this.max}
          disabled={this.loading}
          value={this.values.context}
          oninput={(e: any) => this.set('context', e.target.value)}
        />
        <p className="CmRegister-hint" data-cm-context-left={String(left)}>
          {app.translator.trans('umbral-comunidad.forum.solicitud.hint_context', { count: left })}
        </p>
        {this.message('context')}
      </div>
    );
  }

  checkbox() {
    const error = this.errors.rules;

    return (
      <div className={'CmRegister-check' + (error ? ' CmRegister-field--error' : '')} data-cm-field="rules">
        {/* Mismo guarda que Form A: las normas se pueden leer y copiar (T10). */}
        <label
          onmousedown={rememberCheckLabelPress}
          onclick={keepSelectionOnCheckLabel}
          ondblclick={undoCheckLabelDoubleClick}
        >
          <input
            id="cm-req-rules"
            type="checkbox"
            name="rules"
            checked={this.values.rules}
            disabled={this.loading}
            aria-invalid={error ? 'true' : undefined}
            onchange={(e: any) => {
              this.values.rules = !!e.target.checked;
              this.clear('rules');
            }}
          />
          {/* Las normas y las condiciones son enlaces reales, en pestaña nueva — gemelo del
              Form A, ver `utils/legalLinks.tsx` (PKG-FORO-SMOKE-FIX). */}
          <span>{t2('label_rules', legalLinkParams())}</span>
        </label>
        {this.message('rules')}
      </div>
    );
  }

  message(name: FieldName) {
    const error = this.errors[name];

    if (!error) return null;

    return (
      <p className="CmRegister-msg" data-cm-error={name}>
        {error}
      </p>
    );
  }

  /**
   * `lib.` y no `forum.`: la cola del panel de admin pinta estas MISMAS etiquetas, y Flarum
   * solo manda al admin las claves `admin.` y `lib.` (ver el comentario del locale). Un solo
   * sitio donde escribir «Ninguna aún».
   */
  optionLabel(group: string, value: string) {
    return app.translator.trans(`umbral-comunidad.lib.options.${group}.${value}`);
  }

  set(name: FieldName, value: string) {
    (this.values as any)[name] = value;
    this.clear(name);
  }

  clear(name: FieldName) {
    if (this.errors[name] !== undefined) delete this.errors[name];
  }

  // --- envio --------------------------------------------------------------------------

  onsubmit(e: SubmitEvent) {
    e.preventDefault();

    if (this.loading) return;

    this.unmapped = [];
    this.errors = validate(this.values, this.max);

    if (Object.keys(this.errors).length) {
      this.focusFirstError();
      return;
    }

    this.loading = true;

    app
      .request<any>({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/umbral/group-requests',
        body: { data: { attributes: this.payload() } },
        errorHandler: this.onerror.bind(this),
      })
      .then(
        (payload: any) => {
          this.loading = false;
          this.justSent = true;
          this.retrying = false;

          // El estado local se pone al dia con lo que respondio el servidor, para que la
          // n8card del sidebar y D7 dejen de ofrecer el formulario sin recargar la pagina.
          const data = payload && payload.data;

          setGate({
            request: {
              id: Number(data?.id || 0),
              status: (data?.attributes?.status as any) || 'pending',
              createdAt: data?.attributes?.createdAt || null,
              decidedAt: null,
            },
          });

          m.redraw();
        },
        () => {
          this.loading = false;
          m.redraw();
        }
      );
  }

  payload() {
    const clean: any = {
      origin: this.values.origin,
      excel: this.values.excel,
      n8n: this.values.n8n,
      context: this.values.context.trim() || null,
      rules: this.values.rules,
    };

    MULTI.forEach((name) => {
      clean[name] = this.values[name];
    });

    return clean;
  }

  onerror(error: any) {
    // Primero soltar el candado: `requestErrorCatch` del core llama a este manejador ANTES de
    // rechazar la promesa, y con `loading` en true los campos se rinden deshabilitados —
    // `.focus()` sobre un input deshabilitado no hace nada. Es la misma trampa que costo una
    // corrida en T07.
    this.loading = false;

    const response = error && error.response;

    // Conflicto de estado (409/503): no es de campo. Se corrige la pantalla con lo que el
    // servidor acaba de decir, en vez de dejar un formulario que ya no aplica.
    const conflict = conflictMessage(response);

    /*
      Correo sin confirmar: no es un mensaje mas en la barra del formulario, es un CAMBIO DE
      PANTALLA. Se atiende antes que el resto de conflictos porque `conflictGate()` no sabe
      describirlo (no es un campo de `GateState`) y sin esto el mensaje quedaria arriba de un
      formulario que la persona no puede enviar de ninguna manera.
    */
    if (isEmailUnconfirmedConflict(response)) {
      this.rejectedAsUnconfirmed = true;
      this.errors = {};
      this.unmapped = [];

      // La pantalla entera cambia bajo los pies de quien acaba de pulsar «Enviar», así que el
      // mensaje va como ALERTA y no en la barra del formulario — que deja de existir. Es el
      // mismo reparto que usan los otros conflictos de estado unas líneas más abajo: la pantalla
      // nueva dice DÓNDE está la persona y la alerta dice POR QUÉ el envío no prosperó.
      if (conflict) app.alerts.show({ type: 'warning' }, conflict);

      m.redraw();

      return;
    }

    if (conflict) {
      const next = conflictGate(response);

      this.errors = {};

      if (next) {
        /*
          El estado cambia, asi que `body()` deja de pintar `form()` — y `unmapped` SOLO se
          pinta dentro del formulario. Dejar ahi el mensaje lo haria invisible: se probo y no
          aparecia por ninguna parte. Va como alerta, que ademas es lo correcto: la pantalla
          nueva explica DONDE esta la solicitud, y la alerta explica POR QUE el envio no
          prosperó (ya eras miembro / ya tenias una).
        */
        setGate(next);
        this.unmapped = [];
        app.alerts.show({ type: 'warning' }, conflict);
      } else {
        // Sin cambio de estado (p. ej. la puerta sin configurar) se sigue viendo el
        // formulario, y ahi la barra de arriba es el sitio natural del mensaje.
        this.unmapped = [conflict];
      }

      m.redraw();

      return;
    }

    const mapped = mapServerErrors(response);

    this.errors = mapped.errors;
    this.unmapped = mapped.unmapped;

    if (!Object.keys(this.errors).length && !this.unmapped.length) {
      if (error && error.alert) app.alerts.show(error.alert, error.alert.content);
      return;
    }

    this.focusFirstError();
  }

  /** Foco al primer error EN EL ORDEN DEL FORMULARIO, no en el que llegaron (M2b). */
  focusFirstError() {
    m.redraw.sync();

    const first = FIELD_ORDER.find((name) => this.errors[name] !== undefined);

    if (!first) return;

    const wrapper = document.querySelector(`[data-cm-field="${first}"]`);
    const focusable = wrapper && (wrapper.querySelector('input, select, textarea, button') as HTMLElement | null);

    if (focusable) focusable.focus();
    if (wrapper) wrapper.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }

  t(key: string) {
    return app.translator.trans('umbral-comunidad.forum.solicitud.' + key);
  }
}

/** Traduccion con parametros (el `t` del componente es de una sola clave). */
function t2(key: string, params: any) {
  return app.translator.trans('umbral-comunidad.forum.solicitud.' + key, params);
}
